import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ResultsFeedbackModal } from '../components/results/ResultsFeedbackModal';
import type { FeedbackContextData } from '../lib/types/feedback';

interface FeedbackModalContextValue {
  enabled: boolean;
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  setContextData: (data: FeedbackContextData) => void;
  contextData: FeedbackContextData;
}

const FeedbackModalContext = createContext<FeedbackModalContextValue>({
  enabled: false,
  isOpen: false,
  openModal: () => undefined,
  closeModal: () => undefined,
  setContextData: () => undefined,
  contextData: {},
});

interface FeedbackModalProviderProps {
  enabled?: boolean;
  children: React.ReactNode;
}

export const FeedbackModalProvider: React.FC<FeedbackModalProviderProps> = ({
  enabled = false,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contextData, setContextData] = useState<FeedbackContextData>({});

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openModal = useCallback(() => {
    if (!enabled) return;
    setIsOpen(true);
  }, [enabled]);

  const handleSetContextData = useCallback((data: FeedbackContextData) => {
    setContextData(data);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
      setContextData({});
    }
  }, [enabled]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen]);

  const value = useMemo(
    () => ({
      enabled,
      isOpen,
      openModal,
      closeModal,
      setContextData: handleSetContextData,
      contextData,
    }),
    [enabled, isOpen, openModal, closeModal, handleSetContextData, contextData]
  );

  return (
    <FeedbackModalContext.Provider value={value}>
      {children}
      {enabled && (
        <ResultsFeedbackModal
          isOpen={isOpen}
          onClose={closeModal}
          contextData={contextData}
        />
      )}
    </FeedbackModalContext.Provider>
  );
};

export const useFeedbackModal = (): FeedbackModalContextValue => {
  return useContext(FeedbackModalContext);
};
